import { Button, Drawer, DrawerBody, DrawerFooter, DrawerHeader, DrawerHeading, H4, Row, useOpen } from "@dgs/core";
import { Chart, ChartConfiguration, ChartData } from "chart.js";
import { useLayoutEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { ChartColumn } from "./shared";

const Legend = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	flex: 1;
	align-content: end;
`;

const ChartContainer = styled(Row)`
	align-items: flex-start;
	max-width: 400px;
	margin: auto;
`;

const ColorIndicator = styled.div`
	width: 1rem;
	height: 1rem;
`;

function getColor(valueCount: number): string[] {
	const segment = 360 / (valueCount + 1);
	return Array.from({ length: valueCount }, (_, i) => `hsl(${segment * i}, 65%, 50%)`);
}

export interface DoughnutValue {
	label: string;
	value: number;
}

interface Props {
	headline: string;
	values: DoughnutValue[];
	unit?: string;
}

const LegendItems = ({ items, colors, unit }: { items: DoughnutValue[]; colors?: string[]; unit?: string }) => {
	const { t } = useTranslation();
	return items.map((v, index) => {
		const label = v.label === "none" ? t("none") : v.label;
		return (
			<Row key={label}>
				{colors && <ColorIndicator style={{ backgroundColor: colors[index] }} />}
				<span>
					{label}: {v.value}
					{unit}
				</span>
			</Row>
		);
	});
};
const MAX_VISIBLE_ITEMS = 20;
export function DoughnutChart({ values, headline, unit }: Props) {
	const { t } = useTranslation();
	const ref = useRef<HTMLCanvasElement>(null);
	const visibleItems = useMemo(() => values.slice(0, MAX_VISIBLE_ITEMS), [values]);
	const { open, handleOpen, handleClose } = useOpen();
	const { data, colors } = useMemo<{ data: ChartData; colors: string[] }>(() => {
		const colors = getColor(visibleItems.length);

		return {
			colors,
			data: {
				labels: visibleItems.map((v) => (v.label === "none" ? t("none") : v.label)),
				datasets: [
					{
						data: visibleItems.map((v) => v.value),
						backgroundColor: colors.slice(0, MAX_VISIBLE_ITEMS),
					},
				],
			},
		};
	}, [t, visibleItems]);

	const config = useMemo<ChartConfiguration>(() => {
		return {
			type: "doughnut",
			data,
			options: {
				cutout: "70%",
				borderWidth: 1,
				plugins: {
					tooltip: {
						callbacks: {
							label: (context) => `${context.dataset.label || ""}${context.parsed}${unit}`,
						},
					},
				},
			},
		};
	}, [data, unit]);

	useLayoutEffect(() => {
		if (ref.current) {
			const myChart = new Chart(ref.current, config);

			return () => {
				myChart.destroy();
			};
		}
	}, [config]);

	return (
		<>
			<ChartColumn $gap={8}>
				<H4>{headline}</H4>
				<ChartContainer $justifyContent="center">
					<canvas ref={ref} />
				</ChartContainer>
				<Legend>
					<LegendItems items={visibleItems} colors={colors} unit={unit} />
				</Legend>
				{values.length > MAX_VISIBLE_ITEMS && <Button onClick={handleOpen}>{t("Show more")}</Button>}
			</ChartColumn>

			<Drawer open={open} onClose={handleClose}>
				<DrawerHeader onClose={handleClose}>
					<DrawerHeading>{headline}</DrawerHeading>
				</DrawerHeader>
				<DrawerBody>
					<Legend>
						<LegendItems items={values} unit={unit} />
					</Legend>
				</DrawerBody>
				<DrawerFooter>
					<Button onClick={handleClose}>{t("Close")}</Button>
				</DrawerFooter>
			</Drawer>
		</>
	);
}
